'use client';

import styled from 'styled-components';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'next-i18next';
import { InsHashTagSearchModel } from '@/types/anonymously.interface';
import { useEffect, useState } from 'react';
import { message } from 'antd';

const Container = styled.div`
  margin: 0 auto;
  margin-top: 80px;
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0px 2px 12px 0px #e3e6f3;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const HashtagGrid = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
`;

const HashtagRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  /* background: #f8f9fc; */
  cursor: pointer;
  min-height: 50px;

  @media (max-width: 640px) {
    gap: 0.5rem;
    padding: 0.75rem;
  }

  > .valContainer {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    > .btn {
      display: none;
    }
  }

  &:hover {
    background: #f3f4f6;
    color: #6366f1;

    > .valContainer {
      > .btn {
        display: inline-block;
      }
    }
  }
`;

const Hashtag = styled.div`
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  word-break: break-all;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  /* padding: 0.25rem; */
  cursor: pointer;
  opacity: 0.5;
  transition: opacity 0.2s;
  color: #4b5563;

  &:hover {
    opacity: 1;
  }
`;

const Metric = styled.div`
  /* color: #64748b; */
  white-space: nowrap;
  font-size: 0.875rem;
  text-align: right;
  word-wrap: break-all;
`;

const NextButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  margin-top: 2rem;
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #4f46e5;
  }
`;

export default function HashtagListComponent({ hashtags }: { hashtags?: InsHashTagSearchModel[] }) {
  const { t } = useTranslation();
  const [copiedTitle, setCopiedTitle] = useState<string | null>(null);
  const [isMoreData, setIsMoreData] = useState(false);

  useEffect(() => {
    setIsMoreData(false);
  }, [hashtags]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedTitle(text);
      setTimeout(() => setCopiedTitle(null), 2000);
    });
  };

  return (
    <Container style={{ display: hashtags?.length > 0 ? 'block' : 'none' }}>
      <Title>{t('Hashtags')}</Title>
      <HashtagGrid>
        {(isMoreData ? hashtags : hashtags?.length > 30 ? hashtags?.slice(0, 30) : hashtags)?.map((item, index) => (
          <HashtagRow
            key={item?.hashtag + index}
            onClick={() => {
              copyToClipboard(item?.hashtag);
              message.success(t('Copied'));
            }}
          >
            <Hashtag>{item?.hashtag}</Hashtag>

            <div className="valContainer">
              <Metric>{item?.post_num}</Metric>

              <div className="btn">
                <CopyButton aria-label={`Copy ${item?.hashtag}`}>
                  {copiedTitle === item?.hashtag ? <Check size={14} /> : <Copy size={14} />}
                </CopyButton>
              </div>
            </div>
          </HashtagRow>
        ))}
      </HashtagGrid>
      {!isMoreData && hashtags?.length > 30 && (
        <NextButton onClick={() => setIsMoreData(true)}>{t('NEXT PAGE')}</NextButton>
      )}
    </Container>
  );
}
